import { ReactNode, useEffect, useState } from "react";
import { useQueries } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import { List, Typography } from "antd";
import axios from "axios";
import styled from "styled-components";

import { PAD_SCENARIO_SERVICE_ADDRESS } from "../../constants/app.constants";
import { setCheckedPadScenarios, setPadScenario } from "../../store/features";
import PadScenarioModal from "./PadScenarioModal";
import ScenarioItem from "./ScenarioItem";
import { usePadScenario } from "./contexts/usePadScenario";
import useGetScenarioList from "./hooks/useScenarioList";
import useScenarioSticks, { ScenarioStickListKey } from "./hooks/useScenarioSticks";
import { Scenario, Stick } from "./models/scenario";

const Title = Typography.Title;

interface ScenarioListProps {
  title?: ReactNode;
  availablePlays?: string[];
}

export default function ScenarioList({ title, availablePlays = [] }: ScenarioListProps) {
  const { data: scenarios, isLoading, isError } = useGetScenarioList();
  const { state, dispatch: scenarioDispatch } = usePadScenario();
  const { data: sticks } = useScenarioSticks(state.scenario?._id);
  const [showEditModal, setShowEditModal] = useState(false);
  const dispatch = useDispatch();
  const reduxCheckedScenarios = useSelector(
    (state: import("store/rootReducer").RootState) => state.app.checkedPadScenarios
  );

  // Fetch sticks for every checked scenario in parallel.
  // Uses the same query key as useScenarioSticks so results are shared from cache.
  // Sticks stay in React Query cache — they are NOT stored in Redux to avoid
  // large-payload churn. XdaViewer reads sticks directly from this shared cache.
  const API_URL = `${PAD_SCENARIO_SERVICE_ADDRESS}/api/v1/pad-scenario`;
  useQueries(
    state.checkedScenarios.map((scenario) => ({
      queryKey: [ScenarioStickListKey, scenario._id],
      queryFn: (): Promise<Stick[]> =>
        axios.get<Stick[]>(`${API_URL}/${scenario._id.$oid}/sticks`).then((r) => r.data),
      refetchOnWindowFocus: false,
      enabled: !!scenario._id?.$oid
    }))
  );

  useEffect(() => {
    if (scenarios) {
      scenarioDispatch({ type: "SET_SCENARIO_LIST", payload: scenarios });
      if (scenarios.length > 0) {
        const currentScenario = scenarios.find(
          (s) => s.scenarioId === state.scenario.scenarioId
        );
        scenarioDispatch({
          type: "SET_SCENARIO",
          payload: currentScenario ?? scenarios[0]
        });
      } else {
        // No scenarios available — clear selection and checked state.
        scenarioDispatch({
          type: "SET_SCENARIO",
          payload: { scenarioId: "" } as Scenario
        });
        scenarioDispatch({ type: "SET_CHECKED_SCENARIOS", payload: [] });
        dispatch(setCheckedPadScenarios([]));
      }
    }
  }, [scenarios]);

  // Keep context checkedScenarios in sync with Redux whenever Redux changes
  // (handles session restore, project switch restore, and view load).
  useEffect(() => {
    if (!scenarios || scenarios.length === 0) return;
    const reduxIds = new Set(reduxCheckedScenarios.map((s) => s.scenarioId));
    const synced = scenarios.filter((s) => reduxIds.has(s._id.$oid));
    scenarioDispatch({ type: "SET_CHECKED_SCENARIOS", payload: synced });
  }, [reduxCheckedScenarios, scenarios]);

  const handleClick = (item: Scenario) => {
    if (!item.scenarioId || item.scenarioId == state.scenario.scenarioId) {
      return;
    }
    scenarioDispatch({ type: "SET_SCENARIO", payload: item });
    dispatch(
      setPadScenario({
        scenarioId: item.scenarioId,
        play: state.scenario.play,
        showSticksOnMap: true
      })
    );
  };

  useEffect(() => {
    if (sticks) {
      // Sticks stay out of Redux: redux-state-sync would clone the full survey
      // payload into every open tab.
      scenarioDispatch({
        type: "SET_STICKS",
        scenarioId: state.scenario?._id?.$oid,
        payload: sticks
      });
      dispatch(
        setPadScenario({
          scenarioId: state.scenario.scenarioId,
          showSticksOnMap: true,
          play: state.scenario.play
        })
      );
    }
  }, [sticks]);

  const renderItem = (item: Scenario) => (
    <ScenarioItem
      item={item}
      onSelect={handleClick}
      onRequestEdit={() => setTimeout(() => setShowEditModal(true), 100)}
    />
  );

  if (isLoading) {
    return <RootContainer>Loading...</RootContainer>;
  }
  if (isError) {
    return <RootContainer>Error</RootContainer>;
  }

  return (
    <RootContainer>
      <TitleWrapper>{title ? title : <Title level={5}>Scenarios</Title>}</TitleWrapper>
      <List dataSource={state.scenarioList} renderItem={renderItem} />
      <PadScenarioModal
        open={showEditModal}
        mode={"edit"}
        scenario={state.scenario}
        onClose={() => setShowEditModal(false)}
        availablePlays={availablePlays}
      />
    </RootContainer>
  );
}
const RootContainer = styled.div`
  min-width: 200px;
  padding: 5px 10px;
  height: 100%;
  overflow-y: auto;
  max-width: 300px;
`;
const TitleWrapper = styled.div`
  padding: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
